import React, { useState, useEffect, useContext, useCallback } from 'react';
import { Pencil, DollarSign, Ban } from 'lucide-react';
import Swal from 'sweetalert2';
import apiClient, { get, post, patch, del } from '../../services/api/apiClient.js';
import { AuthContext } from '../../contexts/AuthContext.jsx';
import DataTable from '../../components/common/DataTable.jsx';
import Pagination from '../../components/common/Pagination.jsx';
import Modal from '../../components/common/Modal.jsx';
import { formatMoney, formatDate, today, daysAgo, primero, useLocalTable } from './shared.js';
import FiltroFechas from './FiltroFechas.jsx';
import { RESOURCES_FINANZAS_CXP } from '../../config/resources.finanzas-cxp.js';
import { buildTablePdf, exportTableExcel } from '../../utils/exportUtils.js';
import EmailModal from '../../components/common/EmailModal.jsx';
import { enviarCorreoAdjunto, plantillaCorreoV1 } from '../../services/api/mail.js';

const cfg = RESOURCES_FINANZAS_CXP['anticipos-proveedores'];

function AplicarAnticipoModal({ open, onClose, anticipo, onAplicado }) {
  const [lineas, setLineas] = useState([]);
  const [cargando, setCargando] = useState(false);
  const [enviando, setEnviando] = useState(false);

  useEffect(() => {
    if (!open || !anticipo) return;
    setCargando(true);
    Promise.all([
      get(`/gastos/proveedor/${anticipo._idProveedor}/por-pagar`),
      get(`/gastos-contenedor/proveedor/${anticipo._idProveedor}/por-pagar`),
    ]).then(([g, gc]) => {
      const lineasG = (g || []).map((a) => ({
        id: a._id, tipo: 'gastos', fecha: a.fecha, nota: String(a.idGasto),
        total_general: a.total, saldo: a.saldo, importe: 0, observaciones: 'NA',
      }));
      const lineasGC = (gc || []).map((a) => ({
        id: a._id, tipo: 'gastosContenedor', fecha: a.fecha, nota: String(a.idGastoContenedor),
        total_general: a.total, saldo: a.saldo, importe: 0, observaciones: 'NA',
      }));
      setLineas([...lineasGC, ...lineasG]);
    }).finally(() => setCargando(false));
  }, [open, anticipo]);

  function actualizaImporte(id, valor) {
    setLineas((prev) => prev.map((l) => {
      if (l.id !== id) return l;
      const num = Number(valor) || 0;
      if (num > l.saldo) {
        Swal.fire({ icon: 'error', title: 'Oops...', text: 'El Pago excede el Saldo' });
        return l;
      }
      return { ...l, importe: num };
    }));
  }

  function actualizaObservaciones(id, valor) {
    setLineas((prev) => prev.map((l) => (l.id === id ? { ...l, observaciones: valor } : l)));
  }

  const totalAplicar = lineas.reduce((acc, l) => acc + (Number(l.importe) || 0), 0);

  async function aplicar() {
    if (totalAplicar > anticipo.saldo) {
      Swal.fire({ icon: 'error', title: 'Oops...', text: 'El Importe a aplicar no coincide con el total de la Devolucion' });
      return;
    }
    const r = await Swal.fire({
      title: 'Estas seguro?', text: 'Se registrará el abono', icon: 'warning',
      showCancelButton: true, confirmButtonColor: '#3085d6', cancelButtonColor: '#d33', confirmButtonText: 'Si, Enviar!',
    });
    if (!r.isConfirmed) return;

    setEnviando(true);
    try {
      const seleccionadas = lineas.filter((l) => Number(l.importe) > 0);
      for (const l of seleccionadas) {
        const payload = {
          fecha: today(), importe: Number(l.importe), proveedores: anticipo._idProveedor,
          formaPago: `Anticipo ${anticipo.numero} Aplicado`, observaciones: l.observaciones, tipoCambio: 1,
        };
        if (l.tipo === 'gastosContenedor') payload.gastosContenedor = l.id;
        else payload.gastos = l.id;
        await post('/pagos/aplicar-anticipo', payload);
      }
      await patch(`/${cfg.route}/${anticipo._id}/aplicar-abono`, { utilizado: totalAplicar, saldo: totalAplicar });
      Swal.fire('Good job!', 'Creado con exito', 'success');
      onAplicado();
      onClose();
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'No se pudo aplicar', text: err.message });
    } finally {
      setEnviando(false);
    }
  }

  if (!anticipo) return null;

  return (
    <Modal open={open} onClose={onClose} title={`Aplicar Anticipo #${anticipo.numero} - Proveedor ${anticipo.proveedor}`} width="max-w-3xl"
      footer={(
        <>
          <button type="button" onClick={onClose} className="btn-outline">Cerrar</button>
          <button type="button" disabled={enviando} onClick={aplicar} className="btn-primary">
            Registrar
          </button>
        </>
      )}
    >
      <div className="flex justify-end mb-3">
        <span className="text-sm text-slate-500 mr-2">Saldo</span>
        <span className="font-semibold text-danger-600">{formatMoney(anticipo.saldo)}</span>
      </div>
      <div className="overflow-x-auto rounded-lg border border-slate-200">
        <table className="min-w-full text-sm">
          <thead className="bg-slate-50 text-slate-600">
            <tr>
              <th className="px-2 py-2 text-left">Fecha</th>
              <th className="px-2 py-2 text-left">Nota</th>
              <th className="px-2 py-2 text-right">Total</th>
              <th className="px-2 py-2 text-right">Abonos</th>
              <th className="px-2 py-2 text-right">Saldo</th>
              <th className="px-2 py-2 text-right">Importe</th>
              <th className="px-2 py-2 text-left">Observaciones</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {cargando && <tr><td colSpan={7} className="px-3 py-4 text-center text-slate-400">Cargando...</td></tr>}
            {!cargando && lineas.length === 0 && (
              <tr><td colSpan={7} className="px-3 py-4 text-center text-slate-400">Sin partidas pendientes</td></tr>
            )}
            {lineas.map((l) => (
              <tr key={l.id}>
                <td className="px-2 py-2">{formatDate(l.fecha)}</td>
                <td className="px-2 py-2">{l.nota}</td>
                <td className="px-2 py-2 text-right">{formatMoney(l.total_general)}</td>
                <td className="px-2 py-2 text-right">{formatMoney(l.total_general - l.saldo)}</td>
                <td className="px-2 py-2 text-right">{formatMoney(l.saldo)}</td>
                <td className="px-2 py-2 text-right">
                  <input type="number" min="0" step="any" value={l.importe} onChange={(e) => actualizaImporte(l.id, e.target.value)}
                    className="input-base w-28 text-right" />
                </td>
                <td className="px-2 py-2">
                  <input type="text" value={l.observaciones} onChange={(e) => actualizaObservaciones(l.id, e.target.value)}
                    className="input-base" />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="flex justify-end mt-3">
        <span className="text-sm text-slate-500 mr-2">Total</span>
        <span className="font-semibold">{formatMoney(totalAplicar)}</span>
      </div>
    </Modal>
  );
}

/**
 * Modal de edición de Anticipo -- replica `EditAnticipo`/`EditAnticipoSave` de
 * `ListadoAnticiposProveedores.jsx` (V1): permite reasignar fecha, folio,
 * proveedor, banco, forma de pago, importe (con el mismo candado `importe >=
 * utilizado` que valida el backend en `anticipos-proveedores.service.js#editar`)
 * y observaciones. V2 no tenía esta función en absoluto -- solo Crear/Aplicar/
 * Cancelar -- gap real encontrado en la auditoría 2026-09-28.
 */
function EditarAnticipoModal({ open, onClose, anticipo, proveedores, bancos, onEditado }) {
  const [v, setV] = useState(null);
  const [enviando, setEnviando] = useState(false);

  useEffect(() => {
    if (!open || !anticipo) return;
    setV({
      fecha: (anticipo.fecha || '').slice(0, 10),
      folio: anticipo.folio || '',
      proveedores: anticipo._idProveedor || '',
      bancos: anticipo._idBanco || '',
      formaPago: anticipo.formaPago || '',
      importe: anticipo.importe,
      observaciones: anticipo.observaciones || '',
    });
  }, [open, anticipo]);

  if (!open || !anticipo || !v) return null;

  function set(k, val) { setV((s) => ({ ...s, [k]: val })); }

  async function guardar(e) {
    e.preventDefault();
    const nuevoImporte = Number(v.importe);
    if (nuevoImporte < anticipo.utilizado) {
      Swal.fire({ icon: 'error', title: 'Error de validación', text: `El importe no puede ser menor a lo ya utilizado: ${formatMoney(anticipo.utilizado)}` });
      return;
    }
    const r = await Swal.fire({
      title: '¿Estás seguro?', text: 'Se actualizará el anticipo con todos los cambios', icon: 'warning',
      showCancelButton: true, confirmButtonColor: '#3085d6', cancelButtonColor: '#d33', confirmButtonText: 'Sí, actualizar!',
    });
    if (!r.isConfirmed) return;

    setEnviando(true);
    try {
      await patch(`/${RESOURCES_FINANZAS_CXP['anticipos-proveedores'].route}/${anticipo._id}`, {
        fecha: v.fecha, importe: nuevoImporte, saldo: nuevoImporte - anticipo.utilizado,
        proveedores: v.proveedores, bancos: v.bancos, formaPago: v.formaPago,
        observaciones: v.observaciones, folio: v.folio,
      });
      Swal.fire('¡Perfecto!', 'Anticipo actualizado con éxito', 'success');
      onEditado();
      onClose();
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'No se pudo actualizar', text: err.message });
    } finally {
      setEnviando(false);
    }
  }

  return (
    <Modal open={open} onClose={onClose} title={`Editar Anticipo ${anticipo.numero} - ${anticipo.proveedor}`} width="max-w-2xl"
      footer={(
        <>
          <button type="button" onClick={onClose} className="btn-outline">Cerrar</button>
          <button type="submit" form="form-editar-anticipo" disabled={enviando} className="btn-primary">
            Guardar Cambios
          </button>
        </>
      )}
    >
      <form id="form-editar-anticipo" onSubmit={guardar} className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className="label">Fecha</label>
          <input type="date" required value={v.fecha} onChange={(e) => set('fecha', e.target.value)}
            className="input-base" />
        </div>
        <div>
          <label className="label">Folio</label>
          <input type="text" required value={v.folio} onChange={(e) => set('folio', e.target.value)}
            className="input-base" />
        </div>
        <div>
          <label className="label">Proveedor</label>
          <select required value={v.proveedores} onChange={(e) => set('proveedores', e.target.value)}
            className="select-base">
            <option value="">Selecciona un Proveedor</option>
            {[...proveedores].sort((a, b) => (a.name > b.name ? 1 : -1)).map((p) => (
              <option key={p._id} value={p._id}>{p.name}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="label">Banco</label>
          <select required value={v.bancos} onChange={(e) => set('bancos', e.target.value)}
            className="select-base">
            <option value="">Selecciona un Banco</option>
            {[...bancos].sort((a, b) => (a.banco > b.banco ? 1 : -1)).map((b) => (
              <option key={b._id} value={b._id}>{b.banco} {b.cuenta}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="label">Importe</label>
          <input type="number" step="0.01" min={anticipo.utilizado} required value={v.importe} onChange={(e) => set('importe', e.target.value)}
            className="input-base" />
          {anticipo.utilizado > 0 && (
            <small className="text-slate-400">Mínimo permitido: {formatMoney(anticipo.utilizado)}</small>
          )}
        </div>
        <div>
          <label className="label">Forma de Pago</label>
          <select required value={v.formaPago} onChange={(e) => set('formaPago', e.target.value)}
            className="select-base">
            <option value="">Selecciona una Forma de Pago</option>
            <option value="Efectivo">Efectivo</option>
            <option value="Transfer">Transfer</option>
          </select>
        </div>
        <div className="sm:col-span-2">
          <label className="label">Observaciones</label>
          <textarea value={v.observaciones} onChange={(e) => set('observaciones', e.target.value)}
            className="input-base" rows={3} />
        </div>
      </form>
    </Modal>
  );
}

function valoresIniciales() {
  return { fecha: today(), proveedores: '', bancos: '', formaPago: '', folio: '', importe: 0, observaciones: '' };
}

function AnticipoForm({ open, onClose, proveedores, bancos, onSubmit }) {
  const [v, setV] = useState(valoresIniciales());
  const [guardando, setGuardando] = useState(false);

  useEffect(() => { if (open) setV(valoresIniciales()); }, [open]);

  function set(k, val) { setV((s) => ({ ...s, [k]: val })); }

  async function handleSubmit(e) {
    e.preventDefault();
    if (!v.proveedores || !v.bancos || !v.folio || !v.importe) {
      Swal.fire({ icon: 'error', title: 'Faltan datos', text: 'Proveedor, banco, folio e importe son obligatorios' });
      return;
    }
    setGuardando(true);
    try {
      await onSubmit(v);
      onClose();
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'No se pudo guardar', text: err.message });
    } finally {
      setGuardando(false);
    }
  }

  return (
    <Modal open={open} onClose={onClose} title="Nuevo Anticipo" width="max-w-2xl"
      footer={(
        <>
          <button type="button" onClick={onClose} className="btn-outline">Cancelar</button>
          <button type="submit" form="anticipo-form" disabled={guardando} className="btn-primary">
            {guardando ? 'Guardando...' : 'Guardar'}
          </button>
        </>
      )}
    >
      <form id="anticipo-form" onSubmit={handleSubmit} className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="sm:col-span-2 sm:w-1/2 sm:pr-2">
          <label className="label">Fecha</label>
          <input type="date" required value={v.fecha} onChange={(e) => set('fecha', e.target.value)}
            className="input-base" />
        </div>
        <div>
          <label className="label">Proveedor</label>
          <select required value={v.proveedores} onChange={(e) => set('proveedores', e.target.value)}
            className="select-base">
            <option value="">Selecciona</option>
            {[...proveedores].sort((a, b) => (a.name > b.name ? 1 : -1)).map((p) => (
              <option key={p._id} value={p._id}>{p.codigo} {p.name}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="label">Forma de Pago</label>
          <select value={v.formaPago} onChange={(e) => set('formaPago', e.target.value)}
            className="select-base">
            <option value="">Selecciona una Forma de Pago</option>
            <option value="Efectivo">Efectivo</option>
            <option value="Transfer">Transfer</option>
          </select>
        </div>
        <div>
          <label className="label">Observaciones</label>
          <input type="text" value={v.observaciones} onChange={(e) => set('observaciones', e.target.value)}
            className="input-base" />
        </div>
        <div>
          <label className="label">Folio</label>
          <input type="text" required value={v.folio} onChange={(e) => set('folio', e.target.value)}
            className="input-base" />
        </div>
        <div>
          <label className="label">Banco</label>
          <select value={v.bancos} onChange={(e) => set('bancos', e.target.value)}
            className="select-base">
            <option value="">Selecciona un Banco</option>
            {[...bancos].filter((b) => b.moneda === 'MXN').sort((a, b) => (a.banco > b.banco ? 1 : -1)).map((b) => (
              <option key={b._id} value={b._id}>{b.banco} {b.cuenta}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="label">Importe</label>
          <input type="number" min="0" step="any" value={v.importe} onChange={(e) => set('importe', e.target.value)}
            className="input-base" />
        </div>
      </form>
    </Modal>
  );
}

export default function AnticiposProveedoresPage() {
  const { hasPermiso } = useContext(AuthContext);
  const puedeVer = hasPermiso(`${cfg.recurso}:ver`);
  const puedeCrear = hasPermiso(`${cfg.recurso}:crear`);
  const puedeEditar = hasPermiso(`${cfg.recurso}:editar`);
  const puedeEliminar = hasPermiso(`${cfg.recurso}:eliminar`);

  const [anticipos, setAnticipos] = useState([]);
  const [proveedores, setProveedores] = useState([]);
  const [bancos, setBancos] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState(null);

  const [formOpen, setFormOpen] = useState(false);
  const [aplicarAnticipo, setAplicarAnticipo] = useState(null);
  const [anticipoEditando, setAnticipoEditando] = useState(null);
  const [fechaInicio, setFechaInicio] = useState(daysAgo(30));
  const [fechaFin, setFechaFin] = useState(today());
  const [mailOpen, setMailOpen] = useState(false);
  const [filtroProveedor, setFiltroProveedor] = useState('');
  const [filtroSaldo, setFiltroSaldo] = useState('');

  const cargar = useCallback(async () => {
    setCargando(true);
    setError(null);
    try {
      const [data, provData, bancosData] = await Promise.all([
        get(`/${cfg.route}/fechas/${fechaInicio}/${fechaFin}`),
        apiClient.get('/proveedores', { params: { limit: 500 } }).then((r) => r.data.data),
        apiClient.get('/bancos', { params: { limit: 500 } }).then((r) => r.data.data),
      ]);
      setProveedores(provData || []);
      setBancos(bancosData || []);
      setAnticipos((data || []).map((a) => {
        const prov = primero(a.proveedores);
        const banco = primero(a.bancos);
        return {
          _id: a._id,
          numero: a.idAnticipoProveedores,
          fecha: a.fecha,
          folio: a.folio,
          proveedor: prov?.name || '—',
          _idProveedor: prov?._id,
          banco: banco ? `${banco.banco} ${banco.cuenta || ''}`.trim() : '—',
          _idBanco: banco?._id,
          formaPago: a.formaPago,
          observaciones: a.observaciones,
          importe: a.importe,
          utilizado: a.utilizado,
          saldo: a.saldo,
          is_active: a.is_active,
        };
      }));
    } catch (e) {
      setError(e.message);
    } finally {
      setCargando(false);
    }
  }, [fechaInicio, fechaFin]);

  useEffect(() => { cargar(); }, [cargar]);

  const { search, setSearch, page, setPage, sortBy, sortOrder, ordenarPor, pageRows, filtradas, pagination } = useLocalTable(
    anticipos.filter((a) => (!filtroProveedor || a._idProveedor === filtroProveedor)
      && (filtroSaldo !== 'pendientes' || a.saldo !== 0) && (filtroSaldo !== 'pagadas' || a.saldo === 0)),
    { searchFields: ['proveedor'], defaultSortBy: 'fecha', defaultSortOrder: 'desc', perPage: 50 },
  );

  // Columnas EXACTAS de V1 (`ListadoAnticiposProveedores.jsx` -> `PDFTabla()`/
  // `excel()`): solo Fecha, Proveedor e Importe -- V1 NO incluye Folio, Número,
  // Banco, Utilizado, Saldo ni Estatus en el PDF/Excel, aunque sí los muestra en
  // la tabla en pantalla.
  const columnasExport = [
    { key: 'fecha', label: 'Fecha' },
    { key: 'proveedor', label: 'Proveedor' },
    { key: 'importe', label: 'Importe', render: (r) => formatMoney(r.importe) },
  ];
  const columnasExcel = columnasExport.map((c) => (c.key === 'importe' ? { key: 'importe', label: 'Importe' } : c));
  const totalExport = filtradas.reduce((a, r) => a + (r.importe || 0), 0);
  const armarPdf = () => buildTablePdf('Detalle de Anticipos', columnasExport, filtradas, { totalCols: { proveedor: 'Total', importe: formatMoney(totalExport) } });

  async function guardar(valores) {
    const payload = {
      fecha: valores.fecha, importe: Number(valores.importe), formaPago: valores.formaPago,
      bancos: valores.bancos, proveedores: valores.proveedores, utilizado: 0,
      saldo: Number(valores.importe), observaciones: valores.observaciones, folio: valores.folio,
    };
    await post(`/${cfg.route}`, payload);
    await cargar();
  }

  async function cancelarAnticipo(row) {
    const r = await Swal.fire({
      title: 'Estas seguro?', text: '¡Se cancelara el Anticipo!', icon: 'warning',
      showCancelButton: true, confirmButtonColor: '#3085d6', cancelButtonColor: '#d33', confirmButtonText: 'Si, Cancelar!',
    });
    if (!r.isConfirmed) return;
    try {
      await del(`/${cfg.route}/${row._id}`);
      Swal.fire('Listo', 'Anticipo cancelado', 'success');
      await cargar();
    } catch (e) {
      Swal.fire({ icon: 'error', title: 'No se pudo cancelar', text: e.message });
    }
  }

  const columnas = [
    { key: 'fecha', label: 'Fecha', sortable: true, render: (r) => formatDate(r.fecha) },
    { key: 'folio', label: 'Folio', sortable: true },
    { key: 'numero', label: 'Numero', sortable: true },
    { key: 'proveedor', label: 'Proveedor', sortable: true },
    { key: 'banco', label: 'Banco', sortable: true },
    { key: 'importe', label: 'Importe', sortable: true, render: (r) => formatMoney(r.importe) },
    { key: 'utilizado', label: 'Utilizado', sortable: true, render: (r) => formatMoney(r.utilizado) },
    { key: 'saldo', label: 'Saldo', sortable: true, render: (r) => formatMoney(r.saldo) },
    {
      key: '__acciones', label: 'Aplicar', sortable: false,
      render: (row) => {
        if (row.is_active === 'No') return 'CANCELADO';
        return (
          <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
            {puedeEditar && (
              <button
                title="Editar" aria-label="Editar"
                className="p-1.5 rounded-md text-primary-600 hover:bg-primary-50"
                onClick={() => setAnticipoEditando(row)}
              >
                <Pencil size={15} />
              </button>
            )}
            {puedeEliminar && row.utilizado === 0 && (
              <button
                title="Cancelar" aria-label="Cancelar"
                className="p-1.5 rounded-md text-danger-500 hover:bg-slate-100"
                onClick={() => cancelarAnticipo(row)}
              >
                <Ban size={15} />
              </button>
            )}
            {row.saldo > 0 && (
              <button
                title="Aplicar" aria-label="Aplicar"
                className="p-1.5 rounded-md text-success-600 hover:bg-success-50"
                onClick={() => setAplicarAnticipo(row)}
              >
                <DollarSign size={15} />
              </button>
            )}
          </div>
        );
      },
    },
  ];

  if (!puedeVer) return <div className="text-slate-500">No tienes permiso para ver este módulo.</div>;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-xl font-semibold text-slate-900">{cfg.title}</h1>
        {puedeCrear && (
          <button onClick={() => setFormOpen(true)} className="btn-primary">
            Crear Anticipo
          </button>
        )}
      </div>

      <h2 className="text-center text-xl font-semibold text-slate-900">Anticipos</h2>
      <div className="flex flex-wrap items-end gap-3">
        <FiltroFechas inicio={fechaInicio} fin={fechaFin} onInicio={setFechaInicio} onFin={setFechaFin} />
        <div>
          <label className="label">Proveedor</label>
          <select value={filtroProveedor} onChange={(e) => { setFiltroProveedor(e.target.value); setPage(1); }} className="select-base">
            <option value="">Selecciona</option>
            {[...proveedores].sort((a, b) => (a.name > b.name ? 1 : -1)).map((p) => (
              <option key={p._id} value={p._id}>{p.name}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="label">Saldo</label>
          <select value={filtroSaldo} onChange={(e) => { setFiltroSaldo(e.target.value); setPage(1); }} className="select-base">
            <option value="">Selecciona</option>
            <option value="pendientes">Pendiente</option>
            <option value="pagadas">Aplicado</option>
          </select>
        </div>
      </div>

      <div className="flex items-center gap-3">
        <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Buscar..."
          className="input-base w-64" />
        <button type="button" onClick={async () => (await armarPdf()).save('DetalleAnticipos.pdf')}
          className="btn-outline text-xs px-3 py-1.5">PDF</button>
        <button type="button" onClick={() => setMailOpen(true)}
          className="btn-outline text-xs px-3 py-1.5">eMail</button>
        <button type="button" onClick={() => exportTableExcel('Detalle de Anticipos', columnasExcel, filtradas, { fileName: `DetalleAnticipos-${fechaInicio}-${fechaFin}`, sheetName: 'DetalleAnticipos' })}
          className="btn-outline text-xs px-3 py-1.5">Excel</button>
        {error && <span className="text-sm text-danger-600">{error}</span>}
      </div>

      <DataTable columns={columnas} data={pageRows} loading={cargando} sortBy={sortBy} sortOrder={sortOrder} onSort={ordenarPor}
        footer={{ banco: 'TOTAL', importe: formatMoney(pageRows.reduce((a, r) => a + (r.importe || 0), 0)), utilizado: formatMoney(pageRows.reduce((a, r) => a + (r.utilizado || 0), 0)), saldo: formatMoney(pageRows.reduce((a, r) => a + (r.saldo || 0), 0)) }} />
      <Pagination pagination={pagination} page={page} onChange={setPage} />

      <AnticipoForm open={formOpen} onClose={() => setFormOpen(false)} proveedores={proveedores} bancos={bancos} onSubmit={guardar} />
      <EmailModal
        open={mailOpen} onClose={() => setMailOpen(false)} titulo="Enviar Lista de Anticipos"
        onEnviar={async (email) => {
          const doc = await armarPdf();
          await enviarCorreoAdjunto({
            subject: 'Lista de Anticipos', email, fileName: 'ListaAnticipos.pdf', docPdf: doc,
            textoHtml: plantillaCorreoV1('Adjunto encontraras la lista de Anticipos.'),
          });
        }}
      />

      <AplicarAnticipoModal open={!!aplicarAnticipo} onClose={() => setAplicarAnticipo(null)} anticipo={aplicarAnticipo} onAplicado={cargar} />
      <EditarAnticipoModal
        open={!!anticipoEditando} onClose={() => setAnticipoEditando(null)} anticipo={anticipoEditando}
        proveedores={proveedores} bancos={bancos} onEditado={cargar}
      />
    </div>
  );
}
